import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  LoadingState,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { parseRM, senToInput } from '@/features/billing/money';
import {
  useCalendarActions,
  useDepositRules,
  useNoShows,
  type DepositRules,
  type NoShowRow,
} from '@/features/calendar/api';
import { isApiError } from '@/lib/api';
import styles from './calendar.module.css';

/** "No-shows & deposits" (#20): no-shows by patient and the deposit rules. */
export function NoShowsTab() {
  const noShows = useNoShows();
  const rules = useDepositRules();
  const columns: Column<NoShowRow>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (r) => <strong>{r.patient.fullName}</strong>,
    },
    {
      key: 'count',
      header: 'No-shows',
      align: 'right',
      render: (r) => r.noShows,
    },
    {
      key: 'rule',
      header: <span className="visually-hidden">Deposit rule</span>,
      render: (r) =>
        r.depositNeeded ? (
          <Badge tone="warning">Deposit needed for the next booking</Badge>
        ) : null,
    },
  ];
  return (
    <div className={styles.split}>
      <Card title="No-shows by patient" flush>
        <DataTable
          caption="No-shows by patient"
          columns={columns}
          rows={noShows.data?.patients}
          rowKey={(r) => r.patient.id}
          loading={noShows.isLoading}
          error={noShows.error}
          onRetry={() => void noShows.refetch()}
          empty={
            <EmptyState
              title="No no-shows"
              body="Patients who did not come appear here."
            />
          }
        />
      </Card>
      <Card title="Deposit rules">
        {rules.isLoading && <LoadingState />}
        {rules.data && (
          <RulesForm key={JSON.stringify(rules.data)} rules={rules.data} />
        )}
      </Card>
    </div>
  );
}

function RulesForm({ rules }: { rules: DepositRules }) {
  const { hasPermission } = useSession();
  const canEdit = hasPermission('calendar:manage');
  const a = useCalendarActions();
  const toast = useToast();
  const [amounts, setAmounts] = useState<Record<string, string>>(
    Object.fromEntries(
      rules.services.map((s) => [
        s.id,
        s.depositSen ? senToInput(s.depositSen) : '',
      ]),
    ),
  );
  const [after, setAfter] = useState(
    rules.depositAfterNoShows === null ? '' : String(rules.depositAfterNoShows),
  );
  const parsed = rules.services.map((s) => ({
    serviceId: s.id,
    depositSen: amounts[s.id]?.trim() ? parseRM(amounts[s.id]) : null,
  }));
  const invalid =
    parsed.some(
      (p, i) => amounts[rules.services[i].id]?.trim() && !p.depositSen,
    ) ||
    (after.trim() !== '' &&
      !(Number(after) >= 1 && Number.isInteger(Number(after))));
  return (
    <form
      className={styles.stack}
      onSubmit={(e) => {
        e.preventDefault();
        void a.saveDepositRules
          .mutateAsync({
            services: parsed,
            depositAfterNoShows: after.trim() ? Number(after) : null,
          })
          .then(() =>
            toast.show({ tone: 'success', title: 'Deposit rules saved' }),
          )
          .catch((err: unknown) =>
            toast.show({
              tone: 'error',
              title: 'Not saved',
              body: isApiError(err) ? err.message : undefined,
              error: err,
            }),
          );
      }}
    >
      <p className={styles.sub}>
        A deposit holds slots for services that take a long time to prepare. It
        comes off the bill, is refunded if the patient cancels at least 24 hours
        before, and is kept on a no-show.
      </p>
      {rules.services.map((s) => (
        <Field
          key={s.id}
          label={
            <>
              {s.name} (RM) <span className={styles.optional}>optional</span>
            </>
          }
        >
          <Input
            inputMode="decimal"
            value={amounts[s.id] ?? ''}
            disabled={!canEdit}
            onChange={(e) => setAmounts({ ...amounts, [s.id]: e.target.value })}
          />
        </Field>
      ))}
      <Field
        label={
          <>
            Deposit required after this many no-shows{' '}
            <span className={styles.optional}>optional</span>
          </>
        }
      >
        <Input
          inputMode="numeric"
          value={after}
          disabled={!canEdit}
          onChange={(e) => setAfter(e.target.value.replace(/\D/g, ''))}
        />
      </Field>
      {canEdit && (
        <Button
          type="submit"
          variant="primary"
          disabled={invalid}
          loading={a.saveDepositRules.isPending}
        >
          Save rules
        </Button>
      )}
    </form>
  );
}
